import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { useDisabledFields } from "@/modules/common/context/disabled-fields.context";

interface DisabledFieldWrapperProps {
  fieldKey: string;
  disabledFieldKeys?: Set<string>;
  tooltipMessage?: string;
  children: React.ReactNode;
}

/**
 * Wrapper to disable a field with a tooltip (desktop)
 * or inline helper (mobile/tablet)
 *
 * Supports both prop-based (legacy) and context-based (preferred) disabledFieldKeys.
 */
export const DisabledFieldWrapper: React.FC<DisabledFieldWrapperProps> = ({
  fieldKey,
  disabledFieldKeys: propDisabledFieldKeys,
  tooltipMessage = "This filter is locked and cannot be modified.",
  children,
}) => {
  const contextDisabledFieldKeys = useDisabledFields();
  const disabledFieldKeys = contextDisabledFieldKeys || propDisabledFieldKeys;
  const isDisabled = disabledFieldKeys?.has(fieldKey);
  const { isMobile, isTablet } = useScreenSize();
  const isTouchView = isMobile || isTablet;

  if (!isDisabled) return <>{children}</>;

  const disabledStyles = {
    opacity: 0.6,
    pointerEvents: "none" as const,
  };

  if (isTouchView) {
    return (
      <Box sx={{ width: "100%", "& > *": disabledStyles }}>
        {/* Field + Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <Box sx={{ flex: 1 }}>{children}</Box>
          <LockOutlinedIcon
            sx={{
              fontSize: 20,
              color: nawyTheme.nawy.colors.semantic.layer.extension.warning,
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginTop: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontStyle: "italic",
              color: nawyTheme.nawy.colors.semantic.layer.extension.warning,
              textAlign: "center",
            }}
          >
            {tooltipMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Tooltip title={tooltipMessage} arrow>
      <Box sx={{ cursor: "not-allowed", "& > *": disabledStyles }}>
        {children}
      </Box>
    </Tooltip>
  );
};

export default DisabledFieldWrapper;
